/**
 * Referential integrity guards for LiveStore.
 *
 * LiveStore's schema DSL doesn't support FK constraints at the SQLite level.
 * These guards enforce the same invariants at the commit site in composables.
 * Materializers must stay pure (for deterministic replay), so all constraint
 * checking lives here — call assertRef() before store.commit().
 *
 * Declared FK relationships:
 *   timeEntries.workplace_id         → workplaces.id
 *   timeEntries.scheduled_shift_id   → scheduledShifts.id
 *   scheduledShifts.workplace_id     → workplaces.id  (non-nullable; use UNASSIGNED_WORKPLACE_ID)
 *   scheduleTemplates.workplace_id   → workplaces.id  (non-nullable; use UNASSIGNED_WORKPLACE_ID)
 *   importConfigs.workplace_id       → workplaces.id
 */

import { tables } from './schema.js';
import { UNASSIGNED_WORKPLACE_ID } from '@/domain/workplace/Workplace.js';
import type { SqlQueryLike } from '@/db/engine.js';

type Store = { query: (q: any) => any[] };

function exists(
  store: Store,
  table: { select(): { where(params: Record<string, unknown>): SqlQueryLike } },
  id: string,
): boolean {
  return store.query(table.select().where({ id })).length > 0;
}

export function assertWorkplace(store: Store, workplaceId: string | null | undefined): void {
  if (!workplaceId || workplaceId === UNASSIGNED_WORKPLACE_ID) return;
  if (!exists(store, tables.workplaces, workplaceId)) {
    throw new ReferenceError(`Workplace "${workplaceId}" not found`);
  }
}

export function assertScheduledShift(store: Store, shiftId: string | null | undefined): void {
  if (!shiftId) return;
  if (!exists(store, tables.scheduledShifts, shiftId)) {
    throw new ReferenceError(`ScheduledShift "${shiftId}" not found`);
  }
}

/**
 * Guard for time entry create/update — checks workplace and scheduled shift refs.
 */
export function assertTimeEntryRefs(
  store: Store,
  {
    scheduled_shift_id,
    workplace_id,
  }: { scheduled_shift_id?: string | null; workplace_id?: string | null },
): void {
  assertWorkplace(store, workplace_id);
  assertScheduledShift(store, scheduled_shift_id);
}

/**
 * Guard for scheduled shift create/update — checks workplace ref.
 * workplace_id must be set (use UNASSIGNED_WORKPLACE_ID if unknown).
 */
export function assertShiftRefs(store: Store, { workplace_id }: { workplace_id: string }): void {
  assertWorkplace(store, workplace_id);
}

/**
 * Guard for schedule template create/update — checks workplace ref.
 */
export function assertScheduleTemplateRefs(
  store: Store,
  { workplace_id }: { workplace_id: string },
): void {
  assertWorkplace(store, workplace_id);
}

/**
 * Guard for import config save — checks workplace ref.
 */
export function assertImportConfigRefs(
  store: Store,
  { workplace_id }: { workplace_id?: string | null },
): void {
  assertWorkplace(store, workplace_id);
}
